// ---------------------------------------------------------------------------
// The Stock tab's one data hook: resolves a task's floors, their CAD
// take-off, the site's live inventory, and every OTHER task's claims on the
// same material — then hands the tab a plain `evaluate(claim)` so the row
// components stay pure render code with no query/store wiring of their own.
//
// This hook is also THE GROUPING SEAM. The take-off keys its rows by storey,
// so one element type shows up once per storey; `rowsFor(material)` collapses
// them to one row per type (`groupRowsByKind`) and every consumer downstream —
// the evaluation, the element list, the ceiling, the row's unit list — works
// on that grouped form only. Nothing else in the tab re-groups or un-groups.
// `floorOf` is the way back: which floor an element of a grouped row sits on.
//
// The site-wide bits (the three queries, the storey/floor mapping, tz
// gating) live in `useSiteStockContext`, shared with `useStockFlags` — this
// hook adds only what's specific to ONE task: its floors, those floors'
// take-off rows, and the other tasks' claims against them.
// ---------------------------------------------------------------------------

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IClaimElements, ISchedulerTask, ITaskStockClaim } from '@interfaces/IScheduler';
import type { ITakeoffMaterial } from '@interfaces/ICadTakeoff';
import type { Delivery, UntrackedMaterial } from '@interfaces/ILogistics';
import { evaluateClaimIn, type IClaimEvaluation } from './claimEvaluation';
import { claimQuantity, fitElements, takenElements, unitKeyOfLabel } from './claimElements';
import { indexClaimsByMaterial, othersFor, type IOtherClaim } from './claimIndex';
import { groupRowsByKind } from './claimRowOptions';
import { useSiteStockContext } from './useSiteStockContext';
import { taskFloorIds, materialsForFloors, cadRowsFor } from './takeoffByFloor';
import type { IOwnBooking } from './joinOrder';
import { orderLinesFor } from '../../LogisticsPage/lines';
import { unitLabel } from '@d-orca/shared/client/logistics';
import { fold } from '../../LogisticsPage/units';

export type { IOtherClaim } from './claimIndex';

export interface ITaskStock {
  /** True once the take-off, the site logistics and the working site's tz
   *  have all settled (success or error) — see `useSiteStockContext`. */
  ready: boolean;
  /** The site has no CAD take-off at all (see `ISiteStockContext`) — the tab
   *  says so instead of showing an empty material list. */
  takeoffMissing: boolean;
  /** The take-off could not be LOADED (see `ISiteStockContext`) — a neutral
   *  note, since the CAD itself is not the problem. */
  takeoffFailed: boolean;
  cadVersionId: string | null;
  /** Every floor the task's areas sit on, distinct, in catalog (top-down)
   *  order — empty for a task with no location, which sees the whole model. */
  floorIds: string[];
  /** Their catalog names, same order. */
  floorNames: string[];
  /** These floors' take-off rows, UNGROUPED (empty until the CAD query
   *  settles) — the Material pick list's source. Anything that counts or
   *  measures uses `rowsFor` instead. */
  materials: ITakeoffMaterial[];
  /** The material's rows on these floors, grouped to one row per element
   *  type. A resolver rather than a value because the row also needs it for
   *  the material the user is about to pick. */
  rowsFor(materialName: string): ITakeoffMaterial[];
  /** The floor an element sits on — from the ungrouped row it came from —
   *  or null for an element none of these floors holds. */
  floorOf(globalId: string): string | null;
  evaluate(claim: ITaskStockClaim): IClaimEvaluation;
  /** Other tasks' claims for `materialName` whose floors overlap this task's
   *  — the "Claimed by" box's source list. Per material, not per element
   *  type: the site holds one stock per material, so a slab's concrete and a
   *  wall's come out of the same pile. */
  otherClaimsFor(materialName: string): IOtherClaim[];
  /** The elements already held of the material on ANY floor, globalId →
   *  holding task's name — what comes off each type's free elements
   *  (`takenElements`). Other tasks' claims count, and so do THIS task's
   *  other rows for the material (a second row must not book what the first
   *  already did); `exceptClaimId` is the row being edited, whose own
   *  elements are of course free to it. An element id is global, so no floor
   *  filter here. */
  takenFor(materialName: string, exceptClaimId: string): Map<string, string>;
  /** The claim's elements cut down to what the CAD still holds and no other
   *  task has, with the quantity recomputed to match — the CAD-changed
   *  banner's "Set" action. */
  fitToCad(claim: ITaskStockClaim): { elements: IClaimElements[]; quantity: number };
  /** The unit the site's stock for `materialName` is tracked in, or null when
   *  Logistics has no level for it. Stock is at most one row per material
   *  (spec §5.2), so when it exists it OWNS the claim's unit — the row locks
   *  the field to it and only that stock's own Edit tab can change it. */
  stockUnitFor(materialName: string): string | null;
  /** THIS task's booking on an open delivery order (not yet delivered) for
   *  the material — the order whose LINE carries this task among its
   *  `sources`, the newest when several, and the task's SHARE of that line —
   *  or null. What the outcome box measures a claim against once its gap
   *  has been acted on: a claim within the share is ordered, one beyond it
   *  offers to add the difference (contract v13). */
  bookingFor(materialName: string): IOwnBooking | null;
  /** The order of `bookingFor`, or null. */
  deliveryFor(materialName: string): Delivery | null;
  /** The share of `bookingFor`, or null. */
  shareFor(materialName: string): number | null;
  /** The site's OPEN order for the material, whoever raised it — what a
   *  booking with no stock on site may join (see `joinOrder`). Unlike
   *  `deliveryFor` this is not scoped to this task: an order raised by
   *  another task is exactly the one worth joining. */
  openOrderFor(materialName: string): Delivery | null;
  /** The Inventory Levels "not tracked" row for the material — an order
   *  exists, no stock has been recorded — or null. */
  untrackedFor(materialName: string): UntrackedMaterial | null;
  tz: string;
}

export function useTaskStock(
  siteId: string,
  /** The schedule `task` belongs to. Task ids repeat across schedules, so an
   *  order's source matches this task only on BOTH. */
  scheduleNumber: string,
  task: ISchedulerTask,
  allTasks: ISchedulerTask[],
): ITaskStock {
  const { t } = useTranslation();
  const { ready, takeoff, takeoffMissing, takeoffFailed, cadVersionId, inventory, deliveries, untracked, openOrderFor, catalog, mapping, tz } =
    useSiteStockContext(siteId);

  const floorIds = useMemo(() => taskFloorIds(task, catalog), [task, catalog]);
  // The floors the tab NAMES: those with a CAD storey behind them, since a
  // floor without one contributes no materials and would only pad the scope
  // line. A task whose floors all lack a storey keeps its names — the empty
  // material list then reads against a named floor, not "no location".
  const floorNames = useMemo(() => {
    const mapped = new Set(mapping.values());
    const shown = floorIds.filter((id) => mapped.has(id));
    const ids = shown.length > 0 ? shown : floorIds;
    return catalog.floors.filter((f) => ids.includes(f.id)).map((f) => f.name);
  }, [floorIds, catalog, mapping]);

  const materials = useMemo(
    () => (takeoff ? materialsForFloors(takeoff.takeoff, mapping, floorIds) : []),
    [takeoff, mapping, floorIds],
  );

  // Element → floor, off the UNGROUPED rows (each names its storey, and every
  // row in `materials` has a mapped one). What `floorOf` answers from.
  const floorByElement = useMemo(() => {
    const idx = new Map<string, string>();
    for (const row of materials) {
      const floorId = mapping.get(row.storeyLocalId)!;
      for (const el of row.elements) idx.set(el.globalId, floorId);
    }
    return idx;
  }, [materials, mapping]);

  const floorOf = useCallback(
    (globalId: string): string | null => floorByElement.get(globalId) ?? null,
    [floorByElement],
  );

  // Every task's claims by folded material name — built once per render of
  // the underlying task list rather than re-walked per row/evaluate() call.
  const claimIndex = useMemo(() => indexClaimsByMaterial(allTasks), [allTasks]);

  const othersOf = useCallback(
    (materialName: string) => othersFor(claimIndex, materialName, task.id, floorIds),
    [claimIndex, task.id, floorIds],
  );

  const otherClaimsFor = useCallback(
    (materialName: string): IOtherClaim[] => othersOf(materialName).overlapping,
    [othersOf],
  );

  const takenFor = useCallback(
    (materialName: string, exceptClaimId: string): Map<string, string> => {
      const key = fold(materialName);
      const ownRows = (task.stockClaims ?? [])
        .filter((c) => c.id !== exceptClaimId && fold(c.materialName) === key)
        .map((c) => ({ taskName: task.name, elements: c.elements ?? [] }));
      return takenElements([...othersOf(materialName).all, ...ownRows]);
    },
    [othersOf, task.stockClaims, task.name],
  );

  const rowsFor = useCallback(
    (materialName: string): ITakeoffMaterial[] => groupRowsByKind(cadRowsFor(materials, materialName)),
    [materials],
  );

  /** A delivery order already raised from (or joined by) THIS task for the
   *  material — the Stock tab stamps the task (schedule + id) and its share
   *  on the material's LINE `sources`, so this is the record that the claim's
   *  shortfall has been acted on, and of how much of it was. The NEWEST when
   *  several, by `createdAt`: the list arrives sorted by urgency, which says
   *  nothing about which order was raised last. `createdAt` is null on rows
   *  that predate the stamp; those sort oldest, which is what they are. */
  const bookingFor = useCallback(
    (materialName: string): IOwnBooking | null => {
      let newest: IOwnBooking | null = null;
      for (const { order, line } of orderLinesFor(deliveries, materialName)) {
        if (order.deliveredAt !== null) continue;
        const mine = line.sources.find((s) => s.scheduleNumber === scheduleNumber && s.taskId === task.id);
        if (mine === undefined) continue;
        if (newest === null || (order.createdAt ?? '') > (newest.order.createdAt ?? '')) {
          newest = { order, share: mine.quantity };
        }
      }
      return newest;
    },
    [deliveries, scheduleNumber, task.id],
  );

  const deliveryFor = useCallback(
    (materialName: string): Delivery | null => bookingFor(materialName)?.order ?? null,
    [bookingFor],
  );

  const shareFor = useCallback(
    (materialName: string): number | null => bookingFor(materialName)?.share ?? null,
    [bookingFor],
  );

  const untrackedFor = useCallback(
    (materialName: string): UntrackedMaterial | null => {
      const key = fold(materialName);
      return untracked.find((m) => fold(m.materialName) === key) ?? null;
    },
    [untracked],
  );

  const stockUnitFor = useCallback(
    (materialName: string): string | null => {
      const level = inventory.find((l) => fold(l.materialName) === fold(materialName));
      // Stock may store a unit KEY ('sheets'); a claim stores what its picker
      // shows, so it adopts the viewer's label ('枚') — `sameUnit` equates them.
      return level ? unitLabel(t, level.unit) : null;
    },
    [inventory, t],
  );

  const evaluate = useCallback(
    (claim: ITaskStockClaim): IClaimEvaluation =>
      evaluateClaimIn(
        claim,
        rowsFor(claim.materialName),
        otherClaimsFor(claim.materialName),
        takenFor(claim.materialName, claim.id),
        inventory,
        cadVersionId,
      ),
    [rowsFor, otherClaimsFor, takenFor, inventory, cadVersionId],
  );

  const fitToCad = useCallback(
    (claim: ITaskStockClaim): { elements: IClaimElements[]; quantity: number } => {
      const rows = rowsFor(claim.materialName);
      const elements = fitElements(rows, claim.elements ?? [], takenFor(claim.materialName, claim.id), floorOf);
      return { elements, quantity: claimQuantity(rows, elements, unitKeyOfLabel(claim.unit)) };
    },
    [rowsFor, takenFor, floorOf],
  );

  // The whole return is memoised, not just the closures inside it: consumers
  // hold the hook's value in `useMemo` deps (`TaskPanelStockTab` maps every
  // claim through `evaluate`), and a fresh object literal per render would
  // make every one of those memos a no-op.
  return useMemo(
    () => ({
      ready,
      takeoffMissing,
      takeoffFailed,
      cadVersionId,
      floorIds,
      floorNames,
      materials,
      rowsFor,
      floorOf,
      evaluate,
      otherClaimsFor,
      takenFor,
      fitToCad,
      stockUnitFor,
      bookingFor,
      deliveryFor,
      shareFor,
      openOrderFor,
      untrackedFor,
      tz,
    }),
    [
      ready, takeoffMissing, takeoffFailed, cadVersionId, floorIds, floorNames, materials,
      rowsFor, floorOf, evaluate, otherClaimsFor, takenFor, fitToCad, stockUnitFor, bookingFor, deliveryFor,
      shareFor, openOrderFor, untrackedFor, tz,
    ],
  );
}
